import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { RotateCcw, XCircle, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { StatusBadge, fmtDate } from './StatusBadge';

const STATUSES = ['', 'pending', 'sent', 'bounced', 'failed', 'suppressed', 'cancelled'];

export function QueueTab({ onChanged }) {
  const { api } = useAuth();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/api/admin/email/queue', { params: { status, search, limit: 100 } });
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load queue');
    }
    setLoading(false);
  }, [api, status, search]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/api/admin/email/queue/${id}/${action}`);
      toast.success(action === 'retry' ? 'Re-queued' : 'Cancelled');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const exportCsv = async () => {
    const res = await api.get('/api/admin/email/export.csv', { params: { status }, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = 'email_queue.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="email-queue-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex flex-wrap gap-1 rounded-full bg-[#041A3B] border border-white/10 p-1">
          {STATUSES.map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded-full text-xs capitalize transition-colors ${status === s ? 'bg-[#00E5FF] text-[#000B18]' : 'text-[#A0B4CC] hover:text-white'}`}
              data-testid={`queue-filter-${s || 'all'}`}>
              {s || 'all'}
            </button>
          ))}
        </div>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search recipient, subject, template"
          className="max-w-xs bg-[#000B18] border-white/10 text-white" data-testid="queue-search-input" />
        <Button variant="ghost" className="text-[#00E5FF] ml-auto" onClick={exportCsv} data-testid="queue-export-btn">
          <Download className="w-4 h-4 mr-2" />Export CSV
        </Button>
      </div>
      <p className="text-xs text-[#5C7599]" data-testid="queue-total">{data.total} item(s)</p>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-[#041A3B] text-[#5C7599] text-xs uppercase">
            <tr><th className="text-left p-3">Recipient</th><th className="text-left p-3">Template</th><th className="text-left p-3">Status</th><th className="text-left p-3">Tries</th><th className="text-left p-3">Last error</th><th className="text-left p-3">Updated</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#00E5FF] inline" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-[#5C7599]" data-testid="queue-empty">Nothing here.</td></tr>
            ) : data.items.map((it) => (
              <tr key={it.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`queue-row-${it.id}`}>
                <td className="p-3 text-white">{it.to_email}<div className="text-xs text-[#5C7599] truncate max-w-[240px]">{it.subject}</div></td>
                <td className="p-3 font-mono text-xs text-[#A0B4CC]">{it.template_key}</td>
                <td className="p-3"><StatusBadge value={it.status} /></td>
                <td className="p-3 text-[#A0B4CC]">{it.attempts}</td>
                <td className="p-3 text-xs text-[#FF9100] max-w-[260px] truncate" title={it.last_error || ''}>{it.last_error || '—'}</td>
                <td className="p-3 text-xs text-[#5C7599] whitespace-nowrap">{fmtDate(it.updated_at)}</td>
                <td className="p-3 whitespace-nowrap text-right">
                  {['failed', 'cancelled', 'bounced', 'suppressed'].includes(it.status) && (
                    <Button size="sm" variant="ghost" className="text-[#00E5FF]" onClick={() => act(it.id, 'retry')} data-testid={`queue-retry-${it.id}`}><RotateCcw className="w-4 h-4" /></Button>
                  )}
                  {it.status === 'pending' && (
                    <Button size="sm" variant="ghost" className="text-[#FF5252]" onClick={() => act(it.id, 'cancel')} data-testid={`queue-cancel-${it.id}`}><XCircle className="w-4 h-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
